/**
 * The session: one fetch of `GET /api/v1/auth/me`, and everything the shell renders
 * from it.
 *
 * ── What the shell renders while `/auth/me` is in flight (ADR-002 §4.4) ─────
 *
 * `status: 'loading'`, and the sidebar renders **placeholder bars and no menu
 * items**. Not a cached menu, not the previous session's menu, not a role-based
 * guess. The reason is specific rather than aesthetic: a guessed menu that is wider
 * than the answer flashes items the person is not entitled to and then removes them,
 * which reads as a permission being revoked mid-session and generates a support
 * ticket; a guess that is narrower hides work they can do. There is no third option
 * where a guess is safe, because the whole point of Task 15's effective-permission
 * fix is that the answer is not derivable from anything the client already knows.
 *
 * The cost is one round trip of empty chrome on a cold load. That is the correct
 * price for never showing a menu that is wrong.
 *
 * ── State, deliberately not a store ────────────────────────────────────────
 *
 * One `useReducer` over a four-case union. 02 §2.1 fixes no client state library and
 * ADR-002 declines to add one: the entire application state at this layer is "who is
 * signed in", it is written by exactly three actions, and the alternative is a
 * dependency plus a middleware plus a devtools bridge for a value that changes on
 * login, on logout and on refresh.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  type ReactNode,
} from 'react';

import { createApiClient, type ApiClient } from '../api/client';
import type { WorkforceApiError } from '../api/errors';
import { isUnauthenticated, isWorkforceApiError } from '../api/errors';
import type { SessionUser } from '../api/types';

export type SessionStatus = 'loading' | 'authenticated' | 'anonymous' | 'failed';

export interface SessionState {
  readonly status: SessionStatus;
  readonly user: SessionUser | null;
  /** Set only in `failed`: a transport error or a 5xx, never a 401. */
  readonly error: WorkforceApiError | null;
}

type Action =
  | { type: 'loading' }
  | { type: 'authenticated'; user: SessionUser }
  | { type: 'anonymous' }
  | { type: 'failed'; error: WorkforceApiError };

const INITIAL: SessionState = { status: 'loading', user: null, error: null };

function reduce(_state: SessionState, action: Action): SessionState {
  switch (action.type) {
    case 'loading':
      // The previous user is dropped rather than kept while refetching. Keeping it
      // would let a stale menu render over a session that has since changed, which
      // is the same defect as guessing one.
      return INITIAL;
    case 'authenticated':
      return { status: 'authenticated', user: action.user, error: null };
    case 'anonymous':
      return { status: 'anonymous', user: null, error: null };
    case 'failed':
      return { status: 'failed', user: null, error: action.error };
  }
}

export interface SessionValue extends SessionState {
  readonly client: ApiClient;
  readonly refresh: () => void;
  readonly signIn: (credentials: { username: string; password: string }) => Promise<void>;
  readonly signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export interface SessionProviderProps {
  readonly children: ReactNode;
  /** Injected in tests; production builds one over the real `fetch`. */
  readonly client?: ApiClient;
}

export function SessionProvider({ children, client }: SessionProviderProps): ReactNode {
  const api = useMemo(() => client ?? createApiClient(), [client]);
  const [state, dispatch] = useReducer(reduce, INITIAL);
  // A counter rather than a boolean: `refresh()` pressed twice must run twice, and a
  // boolean toggling back to its old value would not re-run the effect.
  const [attempt, retry] = useReducer((n: number) => n + 1, 0);

  useEffect(() => {
    const controller = new AbortController();
    dispatch({ type: 'loading' });
    void (async () => {
      try {
        const user = await api.me({ signal: controller.signal });
        if (!controller.signal.aborted) dispatch({ type: 'authenticated', user });
      } catch (cause) {
        if (controller.signal.aborted) return;
        if (isWorkforceApiError(cause)) {
          // A 401 is not a failure of this request; it is the answer. Anyone without
          // a cookie gets one, including a first-time visitor, and rendering an
          // error for the ordinary signed-out case would be a lie.
          dispatch(
            isUnauthenticated(cause) ? { type: 'anonymous' } : { type: 'failed', error: cause },
          );
          return;
        }
        throw cause;
      }
    })();
    return () => {
      controller.abort();
    };
  }, [api, attempt]);

  const signIn = useCallback(
    async (credentials: { username: string; password: string }) => {
      // The login body *is* a `SessionUser` — same server renderer as `/auth/me` —
      // so the shell is fully populated without a second round trip.
      const user = await api.login(credentials);
      dispatch({ type: 'authenticated', user });
    },
    [api],
  );

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      // Local state is cleared whether or not the server answered. A logout that
      // failed upstream still means this tab must stop rendering the session — and
      // the cookie is stateless (D-06), so the honest wording is in
      // `src/modules/auth/routes.ts`: this ends a tab, it does not revoke a token.
      dispatch({ type: 'anonymous' });
    }
  }, [api]);

  const value = useMemo<SessionValue>(
    () => ({ ...state, client: api, refresh: retry, signIn, signOut }),
    [state, api, retry, signIn, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (value === null) {
    throw new Error('useSession was called outside <SessionProvider>. Wrap the tree in one.');
  }
  return value;
}

/**
 * The signed-in user, or a throw.
 *
 * For components that only ever render inside the authenticated shell. The throw is
 * a programming error, not a runtime state: every caller is below a guard that has
 * already established `status === 'authenticated'`.
 */
export function useAuthenticatedUser(): SessionUser {
  const { user } = useSession();
  if (user === null) {
    throw new Error(
      'useAuthenticatedUser was called with no session. It belongs inside the shell.',
    );
  }
  return user;
}

/**
 * Which membership the session is pinned to, or `null` when none is chosen.
 *
 * `active_company_id` may name a company whose row is `enabled: false` — the
 * membership list keeps disabled companies (API_CONTRACT §2.1) precisely so this
 * lookup can find one and the shell can say which company is off.
 */
export function activeCompany(user: SessionUser): SessionUser['companies'][number] | null {
  if (user.active_company_id === null) return null;
  return user.companies.find((c) => c.company_id === user.active_company_id) ?? null;
}
